import React, { PropsWithChildren } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '../../lib/utils';
import { Check } from 'lucide-react';

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuItem = DropdownMenuPrimitive.Item;
const DropdownMenuContent = DropdownMenuPrimitive.Content;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

export interface MenuItem {
  label: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  disabled?: boolean;
  isSelected?: boolean;
  menuitemClass?: string;
}

export interface DropdownProps extends PropsWithChildren {
  menuItems: MenuItem[];
  contentClassName?: string;
  menuItemClassName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onItemClick: (itemValue: any) => void;
  disabled?: boolean;
  dropdownMenuProps?: React.ComponentProps<typeof DropdownMenu>;
  dropdownMenuContentProps?: React.ComponentProps<typeof DropdownMenuContent>;
  align?: 'start' | 'end';
  sideOffset?: number;
  triggerClassName?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  scrollableClassName?: string;
  matchTriggerWidth?: boolean;
}
const Dropdown = ({
  contentClassName,
  menuItems,
  menuItemClassName,
  children,
  disabled,
  onItemClick,
  dropdownMenuProps,
  dropdownMenuContentProps,
  align = 'start',
  sideOffset = 6,
  triggerClassName,
  header,
  footer,
  scrollableClassName,
  matchTriggerWidth = true,
}: DropdownProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleItemClick = (itemValue: any) => {
    onItemClick(itemValue);
  };

  return (
    <DropdownMenu {...dropdownMenuProps} data-test-id="dropdown-menu">
      <DropdownMenuTrigger disabled={disabled} asChild className={cn(triggerClassName)}>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          align={align}
          sideOffset={sideOffset}
          {...dropdownMenuContentProps}
          className={cn(
            'z-50 overflow-hidden rounded-[6px] border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            matchTriggerWidth
              ? 'min-w-[var(--radix-popper-anchor-width)] w-[var(--radix-popper-anchor-width)]'
              : 'w-max min-w-max',
            contentClassName
          )}
        >
          {header}
          <div className={cn(scrollableClassName)}>
            {menuItems.map((item, index) => (
              <DropdownMenuItem
                data-test-id={`dropdown-item-${item.value}`}
                className={cn(
                  'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-[6px] px-2 py-1.5 text-sm outline-none transition-all duration-300 focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                  menuItemClassName,
                  item.menuitemClass
                )}
                key={index}
                onSelect={() => handleItemClick(item.value)}
                disabled={item.disabled}
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center gap-2 w-full">
                  {item.label}
                  {item.isSelected && <Check className="ml-auto h-4 w-4 text-current" />}
                </div>
              </DropdownMenuItem>
            ))}
          </div>
          {footer}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};

export default Dropdown;
